import {
  ConnectionMode,
  type Edge as RFEdge,
  type HandleElement,
  getBezierPath,
  getSmoothStepPath,
  getStraightPath,
  internalsSymbol,
  Position,
  type Node as RFNode,
  type NodeHandleBounds,
} from 'reactflow';
import type { EdgeTypePreference } from '@/types/auth.types';
import type { Edge } from '@/types/node';
import type { NodeId } from 'web';

type Transform = [number, number, number];

/** Node ids that are the `target` of at least one edge — O(edges) to build, O(1) membership. */
export function getNodeIdsWithIncomingEdge(edges: Edge[]): Set<NodeId> {
  const incoming = new Set<NodeId>();
  for (const edge of edges) {
    incoming.add(edge.target);
  }
  return incoming;
}

/**
 * Set of node ids upstream of `rootIds`: walk edges in reverse (each step moves from a
 * node to every edge `source` whose `target` is that node).
 *
 * `rootIds` are usually taken from root nodes in the graph (e.g. workflow outputs).
 */
export function getUpstreamNodes(edges: Edge[], rootIds: readonly NodeId[]): Set<NodeId> {
  const reverseAdj = new Map<string, string[]>();
  for (const edge of edges) {
    const sources = reverseAdj.get(edge.target) ?? [];
    sources.push(edge.source);
    reverseAdj.set(edge.target, sources);
  }

  const reachable = new Set<NodeId>();
  function walk(id: string) {
    if (reachable.has(id)) return;
    reachable.add(id);
    for (const source of reverseAdj.get(id) ?? []) {
      walk(source);
    }
  }
  for (const rootId of rootIds) {
    walk(rootId);
  }
  return reachable;
}

/**
 * Nodes strictly between configured tool inputs and configured tool outputs.
 *
 * A node counts when some configured input can reach it and it can reach some configured output.
 * The inputs and outputs themselves stay out: they keep their own frames. A branch that only feeds
 * the path, or that leaves it without reaching an output, is not between them.
 */
export function getNodesBetweenInputsAndOutputs(
  edges: readonly Edge[],
  inputIds: readonly NodeId[],
  outputIds: readonly NodeId[],
): Set<NodeId> {
  if (inputIds.length === 0 || outputIds.length === 0) {
    return new Set();
  }

  const forward = new Map<string, string[]>();
  const reverse = new Map<string, string[]>();
  for (const edge of edges) {
    const targets = forward.get(edge.source) ?? [];
    targets.push(edge.target);
    forward.set(edge.source, targets);
    const sources = reverse.get(edge.target) ?? [];
    sources.push(edge.source);
    reverse.set(edge.target, sources);
  }

  const walk = (roots: readonly string[], adjacent: Map<string, string[]>) => {
    const reachable = new Set<string>();
    const pending = [...roots];
    while (pending.length > 0) {
      const id = pending.pop();
      if (id === undefined || reachable.has(id)) {
        continue;
      }
      reachable.add(id);
      for (const next of adjacent.get(id) ?? []) {
        pending.push(next);
      }
    }
    return reachable;
  };

  const fromInputs = walk(inputIds, forward);
  const toOutputs = walk(outputIds, reverse);
  const endpoints = new Set<string>([...inputIds, ...outputIds]);
  const between = new Set<NodeId>();
  for (const nodeId of fromInputs) {
    if (toOutputs.has(nodeId) && !endpoints.has(nodeId)) {
      between.add(nodeId);
    }
  }
  return between;
}

export type SelectionRect = {
  x: number;
  y: number;
  width: number;
  height: number;
  startX?: number;
  startY?: number;
};

/** Matches @reactflow/core getHandle(bounds, handleId) — bounds is a handle array */
function getHandleFromList(
  bounds: HandleElement[] | null | undefined,
  handleId: string | null | undefined,
): HandleElement | null {
  if (!bounds?.length) {
    return null;
  }
  if (bounds.length === 1 || !handleId) {
    return bounds[0] ?? null;
  }
  return bounds.find((d) => d.id === handleId) ?? null;
}

function getHandlePosition(
  position: Position,
  nodeRect: { x: number; y: number; width: number; height: number },
  handle: HandleElement | null,
): { x: number; y: number } {
  const x = (handle?.x || 0) + nodeRect.x;
  const y = (handle?.y || 0) + nodeRect.y;
  const width = handle?.width || nodeRect.width;
  const height = handle?.height || nodeRect.height;
  switch (position) {
    case Position.Top:
      return { x: x + width / 2, y };
    case Position.Right:
      return { x: x + width, y: y + height / 2 };
    case Position.Bottom:
      return { x: x + width / 2, y: y + height };
    case Position.Left:
      return { x, y: y + height / 2 };
    default:
      return { x: x + width / 2, y: y + height / 2 };
  }
}

function getNodeData(
  node: RFNode | undefined,
): [{ x: number; y: number; width: number; height: number }, NodeHandleBounds | null, boolean] {
  const handleBounds = node?.[internalsSymbol]?.handleBounds ?? null;
  const isValid = Boolean(
    handleBounds &&
    node?.width &&
    node?.height &&
    typeof node.positionAbsolute?.x !== 'undefined' &&
    typeof node.positionAbsolute?.y !== 'undefined',
  );
  return [
    {
      x: node?.positionAbsolute?.x ?? 0,
      y: node?.positionAbsolute?.y ?? 0,
      width: node?.width ?? 0,
      height: node?.height ?? 0,
    },
    handleBounds,
    isValid,
  ];
}

function getEdgeEndpoints(
  edge: RFEdge,
  sourceNode: RFNode | undefined,
  targetNode: RFNode | undefined,
  connectionMode: ConnectionMode,
): {
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  sourcePosition: Position;
  targetPosition: Position;
} | null {
  const [sourceNodeRect, sourceHandleBounds, sourceIsValid] = getNodeData(sourceNode);
  const [targetNodeRect, targetHandleBounds, targetIsValid] = getNodeData(targetNode);
  if (!sourceIsValid || !targetIsValid || !sourceHandleBounds || !targetHandleBounds) {
    return null;
  }

  const targetNodeHandles: HandleElement[] =
    connectionMode === ConnectionMode.Strict
      ? (targetHandleBounds.target ?? [])
      : [...(targetHandleBounds.target ?? []), ...(targetHandleBounds.source ?? [])];

  const sourceHandle = getHandleFromList(sourceHandleBounds.source, edge.sourceHandle);
  const targetHandle = getHandleFromList(targetNodeHandles, edge.targetHandle);

  if (!sourceHandle || !targetHandle) {
    return null;
  }

  const sourcePosition = sourceHandle.position ?? Position.Bottom;
  const targetPosition = targetHandle.position ?? Position.Top;

  const sourcePos = getHandlePosition(sourcePosition, sourceNodeRect, sourceHandle);
  const targetPos = getHandlePosition(targetPosition, targetNodeRect, targetHandle);

  return {
    sourceX: sourcePos.x,
    sourceY: sourcePos.y,
    targetX: targetPos.x,
    targetY: targetPos.y,
    sourcePosition,
    targetPosition,
  };
}

/** Same transform as React Flow's getNodesInside: pane pixels → flow coordinates */
export function paneRectToFlowRect(
  rect: SelectionRect,
  [tx, ty, tScale]: Transform,
): { x: number; y: number; width: number; height: number } {
  return {
    x: (rect.x - tx) / tScale,
    y: (rect.y - ty) / tScale,
    width: rect.width / tScale,
    height: rect.height / tScale,
  };
}

function pointInRect(px: number, py: number, rx: number, ry: number, rw: number, rh: number): boolean {
  return px >= rx && px <= rx + rw && py >= ry && py <= ry + rh;
}

/** One detached path reused for all edge samples (avoids O(edges) createElementNS per selection). */
let reusablePathEl: SVGPathElement | null = null;

function getReusablePathElement(): SVGPathElement | null {
  if (typeof document === 'undefined') {
    return null;
  }
  if (!reusablePathEl) {
    reusablePathEl = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  }
  return reusablePathEl;
}

/** Sample SVG path in flow space; returns true if any sample lies inside the selection rect (with padding). */
function pathIntersectsRectBySampling(
  pathD: string,
  flowRect: { x: number; y: number; width: number; height: number },
  padding: number,
): boolean {
  const pathEl = getReusablePathElement();
  if (!pathEl) {
    return false;
  }
  const rx = flowRect.x - padding;
  const ry = flowRect.y - padding;
  const rw = flowRect.width + 2 * padding;
  const rh = flowRect.height + 2 * padding;

  pathEl.setAttribute('d', pathD);
  const len = pathEl.getTotalLength();
  if (len <= 0) {
    return false;
  }
  const samples = Math.min(48, Math.max(12, Math.ceil(len / 8)));
  for (let i = 0; i <= samples; i++) {
    const pt = pathEl.getPointAtLength((len * i) / samples);
    if (pointInRect(pt.x, pt.y, rx, ry, rw, rh)) {
      return true;
    }
  }
  return false;
}

function straightPathIntersectsRect(
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
  flowRect: { x: number; y: number; width: number; height: number },
  padding: number,
): boolean {
  const [pathD] = getStraightPath({ sourceX, sourceY, targetX, targetY });
  return pathIntersectsRectBySampling(pathD, flowRect, padding);
}

function bezierPathIntersectsRect(
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
  sourcePosition: Position,
  targetPosition: Position,
  flowRect: { x: number; y: number; width: number; height: number },
  padding: number,
): boolean {
  const [pathD] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });
  return pathIntersectsRectBySampling(pathD, flowRect, padding);
}

function smoothStepPathIntersectsRect(
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
  sourcePosition: Position,
  targetPosition: Position,
  flowRect: { x: number; y: number; width: number; height: number },
  padding: number,
): boolean {
  const [pathD] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    borderRadius: 16,
  });
  return pathIntersectsRectBySampling(pathD, flowRect, padding);
}

export function getEdgeIdsIntersectingSelectionRect(params: {
  paneRect: SelectionRect;
  transform: Transform;
  edges: RFEdge[];
  nodeInternals: Map<string, RFNode>;
  connectionMode: ConnectionMode;
  edgeType: EdgeTypePreference;
}): string[] {
  const { paneRect, transform, edges, nodeInternals, connectionMode, edgeType } = params;
  if (paneRect.width < 4 || paneRect.height < 4) {
    return [];
  }

  const flowRect = paneRectToFlowRect(paneRect, transform);
  const padding = 6;
  const hit: string[] = [];

  for (const edge of edges) {
    if (edge.hidden) continue;

    const sourceNode = nodeInternals.get(edge.source);
    const targetNode = nodeInternals.get(edge.target);
    const endpoints = getEdgeEndpoints(edge, sourceNode, targetNode, connectionMode);
    if (!endpoints) continue;

    const { sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition } = endpoints;

    let intersects = false;
    if (edgeType === 'line') {
      intersects = straightPathIntersectsRect(sourceX, sourceY, targetX, targetY, flowRect, padding);
    } else if (edgeType === 'bezier') {
      intersects = bezierPathIntersectsRect(
        sourceX,
        sourceY,
        targetX,
        targetY,
        sourcePosition,
        targetPosition,
        flowRect,
        padding,
      );
    } else {
      intersects = smoothStepPathIntersectsRect(
        sourceX,
        sourceY,
        targetX,
        targetY,
        sourcePosition,
        targetPosition,
        flowRect,
        padding,
      );
    }

    if (intersects) {
      hit.push(edge.id);
    }
  }

  return hit;
}
